import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Card,
    CardBody,
    CardTitle,
    Divider,
    PageSection,
    Title,
} from '@patternfly/react-core';
import { useParams } from 'react-router-dom';

import { LogsDisplay } from './components/LogsDisplay';
import { VideoSection } from './components/VideoSection';
import { LiveKitViewerProvider } from '../../providers/LiveKitViewerProvider';
import useWebSocket from 'src/hooks/useWebSocket';
import { useAuthedFetch } from 'src/hooks/useAuthedFetch';
import {
    type ReadinessComponents,
    coerceReadinessComponents,
    deriveAggregateReadiness,
} from 'src/app/utils/readiness';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const DEFAULT_TRACK = 'robot_overhead';
const OPERATOR_REGISTERED_KEY = '__operatorRegistered';

type TeleopStatus = Record<string, any>;

interface CommandStatusEvent {
    key: string;
    label: string;
    at: string | null;
}

const coerceTeleopStatus = (value: unknown): TeleopStatus | null => {
    if (!value) {
        return null;
    }
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === 'object' ? (parsed as TeleopStatus) : null;
        } catch {
            return null;
        }
    }
    if (typeof value === 'object') {
        return value as TeleopStatus;
    }
    return null;
};

const formatBool = (value: unknown) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '—';
};

const formatNumber = (value: unknown, digits = 3) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '—';
    }
    return value.toFixed(digits);
};

const formatTimestamp = (value: string | null | undefined) => {
    if (!value) {
        return '—';
    }
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
        return value;
    }
    return new Date(parsed).toLocaleString();
};

const mostRecentTimestamp = (...values: Array<string | null | undefined>): string | null => {
    let latestValue: string | null = null;
    let latestEpoch = Number.NEGATIVE_INFINITY;
    for (const value of values) {
        if (!value) {
            continue;
        }
        const epoch = Date.parse(value);
        if (Number.isNaN(epoch)) {
            continue;
        }
        if (epoch > latestEpoch) {
            latestEpoch = epoch;
            latestValue = value;
        }
    }
    return latestValue;
};

const deriveTeleopStatus = (teleopStatus: TeleopStatus | null) => {
    if (!teleopStatus) {
        return null;
    }
    if (Boolean(teleopStatus.teleop_active) || Boolean(teleopStatus.attempt_active)) {
        return 'Busy';
    }
    if (teleopStatus.ready === true) {
        return 'Ready';
    }
    if (teleopStatus.ready === false) {
        return 'Idle';
    }
    return null;
};

const toBooleanIndicator = (value: unknown): boolean | null => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        if (value.toLowerCase() === 'ready' || value.toLowerCase() === 'available') {
            return true;
        }
        if (value.toLowerCase() === 'not_ready' || value.toLowerCase() === 'unavailable') {
            return false;
        }
    }
    return null;
};

const StatusLight = ({ label, value }: { label: string; value: boolean | null }) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginRight: '12px' }}>
        <span
            style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: value === true ? '#3e8635' : value === false ? '#c9190b' : '#8a8d90',
                display: 'inline-block',
            }}
        />
        <span>{label}</span>
    </div>
);

const extractCommandStatusEvents = (teleopStatus: TeleopStatus | null): CommandStatusEvent[] => {
    if (!teleopStatus) {
        return [];
    }
    const statuses = teleopStatus.command_statuses;
    if (!Array.isArray(statuses)) {
        return [];
    }
    return statuses
        .map((entry, index) => {
            if (!entry || typeof entry !== 'object') {
                return null;
            }
            const typedEntry = entry as Record<string, unknown>;
            const at = typeof typedEntry.at === 'string' ? typedEntry.at : null;
            const command = typeof typedEntry.command === 'string' ? typedEntry.command : 'command';
            const status = typeof typedEntry.status === 'string' ? typedEntry.status : 'unknown';
            return {
                key: `${at || 'no-time'}-${command}-${status}-${index}`,
                label: `${command}: ${status}`,
                at,
            };
        })
        .filter((event): event is CommandStatusEvent => Boolean(event));
};

const RobotStatusPanel = ({
    status,
    teleopStatus,
    readinessComponents,
    teleopStatusAt,
    lastHeartbeatAt,
    commandStatusEvents
}: {
    status: string | null;
    teleopStatus: TeleopStatus | null;
    readinessComponents: ReadinessComponents;
    teleopStatusAt: string | null;
    lastHeartbeatAt: string | null;
    commandStatusEvents: CommandStatusEvent[];
}) => {
    const updatedAt = mostRecentTimestamp(
        teleopStatusAt,
        readinessComponents.publishedAt,
        lastHeartbeatAt
    );
    const derivedReadiness = deriveAggregateReadiness(readinessComponents);
    const readiness = toBooleanIndicator(teleopStatus?.readiness) ?? derivedReadiness;
    const availability = toBooleanIndicator(teleopStatus?.availability) ?? derivedReadiness;
    return (
        <PageSection>
            <Card>
                <CardTitle>Robot Status</CardTitle>
                <CardBody>
                    <div><strong>Status:</strong> {status || '—'}</div>
                    <div><strong>Last update:</strong> {formatTimestamp(updatedAt)}</div>
                    <div style={{ margin: '8px 0' }}>
                        <StatusLight label="Readiness" value={readiness} />
                        <StatusLight label="Availability" value={availability} />
                    </div>
                    <div style={{ margin: '8px 0' }}>
                        <StatusLight label="Control" value={readinessComponents.control} />
                        <StatusLight label="Video" value={readinessComponents.video} />
                        <StatusLight label="Hardware" value={readinessComponents.hardware} />
                    </div>
                    <div><strong>LiveKit connection:</strong> {readinessComponents.livekitConnectionState || '—'}</div>
                    <div><strong>Readiness updated:</strong> {formatTimestamp(readinessComponents.publishedAt)}</div>
                    <div><strong>Ready:</strong> {formatBool(teleopStatus?.ready)}</div>
                    <div><strong>Ready reason:</strong> {teleopStatus?.ready_reason || '—'}</div>
                    <div><strong>Control mode:</strong> {teleopStatus?.control_mode || '—'}</div>
                    <div><strong>Teleop active:</strong> {formatBool(teleopStatus?.teleop_active)}</div>
                    <div><strong>Attempt active:</strong> {formatBool(teleopStatus?.attempt_active)}</div>
                    <div><strong>Attempt result:</strong> {teleopStatus?.attempt_result ?? '—'}</div>
                    <div><strong>Attempt reason:</strong> {teleopStatus?.attempt_reason ?? '—'}</div>
                    <div><strong>Pose error norm:</strong> {formatNumber(teleopStatus?.pose_error_norm)}</div>
                    <div><strong>Teleop msg age (s):</strong> {formatNumber(teleopStatus?.teleop_msg_age_s)}</div>
                    <div><strong>Joint state age (s):</strong> {formatNumber(teleopStatus?.joint_state_age_s)}</div>
                    <div><strong>Gripper state:</strong> {teleopStatus?.gripper_state ?? '—'}</div>
                    <div><strong>Gripper pos:</strong> {formatNumber(teleopStatus?.gripper_pos)}</div>
                    <div><strong>Gripper current:</strong> {formatNumber(teleopStatus?.gripper_current)}</div>
                    <div><strong>IK ok:</strong> {formatBool(teleopStatus?.ik_ok)}</div>
                    <div><strong>Last cmd reject reason:</strong> {teleopStatus?.last_cmd_reject_reason ?? '—'}</div>
                    <div><strong>Last cmd reject count:</strong> {teleopStatus?.last_cmd_reject_count ?? '—'}</div>
                    <Divider style={{ margin: '12px 0' }} />
                    <div><strong>Command statuses:</strong></div>
                    {commandStatusEvents.length === 0 && <div>—</div>}
                    {commandStatusEvents.map((event) => (
                        <div key={event.key}>{formatTimestamp(event.at)} — {event.label}</div>
                    ))}
                    <details>
                        <summary>Raw teleop status</summary>
                        <pre>{teleopStatus ? JSON.stringify(teleopStatus, null, 2) : '—'}</pre>
                    </details>
                </CardBody>
            </Card>
        </PageSection>
    );
};

const RoboViewContent = ({
    videoRef,
    cameraTracks,
    activeCamera,
    onCameraChange,
    onCameraCycle,
    robotStatus,
    teleopStatus,
    readinessComponents,
    teleopStatusAt,
    lastHeartbeatAt,
    commandStatusEvents
}: {
    videoRef: React.RefObject<HTMLVideoElement>;
    cameraTracks: string[];
    activeCamera: string;
    onCameraChange: (trackName: string) => void;
    onCameraCycle: (direction: 'prev' | 'next') => void;
    robotStatus: string | null;
    teleopStatus: TeleopStatus | null;
    readinessComponents: ReadinessComponents;
    teleopStatusAt: string | null;
    lastHeartbeatAt: string | null;
    commandStatusEvents: CommandStatusEvent[];
}) => {
    return (
        <>
            <RobotStatusPanel
                status={robotStatus}
                teleopStatus={teleopStatus}
                readinessComponents={readinessComponents}
                teleopStatusAt={teleopStatusAt}
                lastHeartbeatAt={lastHeartbeatAt}
                commandStatusEvents={commandStatusEvents}
            />

            <VideoSection
                videoRef={videoRef}
                cameraTracks={cameraTracks}
                activeCamera={activeCamera}
                onCameraChange={onCameraChange}
                onCameraCycle={onCameraCycle}
            />

            <LogsDisplay />
        </>
    );
};

export const RoboView = () => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const { robotId } = useParams<{ robotId?: string }>();
    const { sendMessage, connected } = useWebSocket();
    const [clientId, setClientId] = useState<string | null>(null);
    const [robotStatus, setRobotStatus] = useState<string | null>(null);
    const [teleopStatus, setTeleopStatus] = useState<TeleopStatus | null>(null);
    const [readinessComponents, setReadinessComponents] = useState<ReadinessComponents>(
        coerceReadinessComponents(null)
    );
    const [teleopStatusAt, setTeleopStatusAt] = useState<string | null>(null);
    const [roomId, setRoomId] = useState<string | null>(null);
    const [lastHeartbeatAt, setLastHeartbeatAt] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [commandStatusEvents, setCommandStatusEvents] = useState<CommandStatusEvent[]>([]);
    const [cameraTracks, setCameraTracks] = useState<string[]>([]);
    const [activeCamera, setActiveCamera] = useState(DEFAULT_TRACK);
    const authedFetch = useAuthedFetch();
    const operatorName = 'RobotDetails';
    const hasRegisteredRef = useRef(false);

    const cycleCamera = useCallback(
        (direction: 'prev' | 'next') => {
            if (!cameraTracks.length) {
                return;
            }
            const currentIndex = cameraTracks.indexOf(activeCamera);
            const normalizedIndex = currentIndex === -1 ? 0 : currentIndex;
            const nextIndex =
                direction === 'next'
                    ? (normalizedIndex + 1) % cameraTracks.length
                    : (normalizedIndex - 1 + cameraTracks.length) % cameraTracks.length;
            setActiveCamera(cameraTracks[nextIndex]);
        },
        [cameraTracks, activeCamera]
    );

    useEffect(() => {
        if (!cameraTracks.length) {
            return;
        }
        if (!cameraTracks.includes(activeCamera)) {
            setActiveCamera(cameraTracks[0]);
        }
    }, [cameraTracks, activeCamera]);

    useEffect(() => {
        if (!robotId) {
            setClientId(null);
            setRobotStatus(null);
            setTeleopStatus(null);
            setReadinessComponents(coerceReadinessComponents(null));
            setTeleopStatusAt(null);
            setRoomId(null);
            setLastHeartbeatAt(null);
            setLoadError(null);
            setCommandStatusEvents([]);
            return;
        }

        let canceled = false;

        const loadRobot = async () => {
            try {
                setLoadError(null);
                const response = await authedFetch(`${API_BASE}/api/robot/${robotId}`);
                if (!response.ok) {
                    throw new Error(`Failed to load robot: ${response.status}`);
                }
                const data = await response.json();
                if (canceled) {
                    return;
                }
                setClientId(robotId);
                setRobotStatus(data?.status || null);
                setTeleopStatus(coerceTeleopStatus(data?.telemetry?.teleop_status));
                setReadinessComponents(
                    coerceReadinessComponents(data?.telemetry?.readiness_components)
                );
                setTeleopStatusAt(data?.telemetry?.teleop_status_at || null);
                setRoomId(data?.telemetry?.room_id || null);
                setLastHeartbeatAt(data?.last_heartbeat_at || null);
                setCommandStatusEvents(extractCommandStatusEvents(coerceTeleopStatus(data?.telemetry?.teleop_status)));
            } catch {
                if (canceled) {
                    return;
                }
                setLoadError('Unable to load robot details.');
                setClientId(robotId);
            }
        };

        loadRobot();

        return () => {
            canceled = true;
        };
    }, [authedFetch, robotId]);

    useEffect(() => {
        if (!connected) {
            hasRegisteredRef.current = false;
            (window as any)[OPERATOR_REGISTERED_KEY] = false;
            return;
        }
        if (hasRegisteredRef.current) {
            return;
        }
        if ((window as any)[OPERATOR_REGISTERED_KEY]) {
            hasRegisteredRef.current = true;
            return;
        }
        const sent = sendMessage({
            type: 'register',
            handler: 'bot',
            data: {
                id: `operator-${operatorName.toLowerCase()}`,
                name: operatorName,
                type: 'operator',
                status: 'Ready'
            }
        });
        if (sent) {
            hasRegisteredRef.current = true;
            (window as any)[OPERATOR_REGISTERED_KEY] = true;
        }
    }, [connected, sendMessage]);

    useEffect(() => {
        if (!robotId) {
            return;
        }
        let canceled = false;
        const updateTelemetry = async () => {
            try {
                const response = await authedFetch(`${API_BASE}/api/telemetry/robot/${robotId}`);
                if (!response.ok) {
                    return;
                }
                const payload = await response.json();
                if (canceled || !payload) {
                    return;
                }
                const incoming = coerceTeleopStatus(payload.teleop_status);
                setReadinessComponents(
                    coerceReadinessComponents(payload.readiness_components)
                );
                setRoomId(payload.room_id || null);
                if (incoming) {
                    setTeleopStatus(incoming);
                    setTeleopStatusAt(payload.teleop_status_at || null);
                    setCommandStatusEvents(extractCommandStatusEvents(incoming));
                    const derived = deriveTeleopStatus(incoming);
                    if (derived) {
                        setRobotStatus(derived);
                    }
                }
            } catch {
                // Ignore telemetry polling errors to avoid UI churn.
            }
        };

        updateTelemetry();
        const interval = window.setInterval(updateTelemetry, 1000);
        return () => {
            canceled = true;
            window.clearInterval(interval);
        };
    }, [authedFetch, robotId]);

    return (
        <>
            {!robotId && (
                <PageSection>
                    <Title headingLevel="h3">Select a robot from the Dashboard.</Title>
                </PageSection>
            )}
            {loadError && (
                <PageSection>
                    <Title headingLevel="h4">{loadError}</Title>
                </PageSection>
            )}
            {clientId && (
                <RoboViewContent
                    videoRef={videoRef}
                    cameraTracks={cameraTracks}
                    activeCamera={activeCamera}
                    onCameraChange={setActiveCamera}
                    onCameraCycle={cycleCamera}
                    robotStatus={robotStatus || deriveTeleopStatus(teleopStatus)}
                    teleopStatus={teleopStatus}
                    readinessComponents={readinessComponents}
                    teleopStatusAt={teleopStatusAt}
                    lastHeartbeatAt={lastHeartbeatAt}
                    commandStatusEvents={commandStatusEvents}
                />
            )}
            {clientId && (
                <LiveKitViewerProvider
                    videoRef={videoRef}
                    robotId={robotId || clientId}
                    roomId={roomId}
                    activeTrackName={activeCamera}
                    onTracksChange={setCameraTracks}
                    onActiveTrackChange={setActiveCamera}
                />
            )}
        </>
    );
};
